import { useSelector } from 'react-redux';
import {
  selectAllPoints,
  selectInterconnects,
  selectIsMapLoading,
} from '../map/slice/mapSelectors';
import { selectConstraintsAtPoints } from '../scenarios/scenarioSlice';
import { titleize } from '../utils/stringUtils';
import { numberFormatter } from '../helpers/formatters';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { TrashIcon } from '@iconicicons/react';
import {
  useDeleteInterconnectMutation,
  useDisableInterconnectMutation,
  useEnableInterconnectMutation,
} from '../services/edgeApi';
import toast from 'react-hot-toast';

/**
 *
 * @typedef {{
 *  interconnectId?: import('../services/edgeApi').Interconnect['id'];
 *  closePopup: () => void;
 * }} InterconnectPopupProps
 */

/** @type {React.FC<InterconnectPopupProps>} */
export const InterconnectPopup = ({ interconnectId, closePopup }) => {
  const [deleteInterconnectTrigger] = useDeleteInterconnectMutation();
  const [enableInterconnectTrigger, enableInterconnectRequestInfo] =
    useEnableInterconnectMutation();
  const [disableInterconnectMutation, disableInterconnectRequestInfo] =
    useDisableInterconnectMutation();

  const location = useLocation();

  const isMapLoading = useSelector(selectIsMapLoading);
  const points = useSelector(selectAllPoints);
  const constraints = useSelector(selectConstraintsAtPoints);
  const interconnects = useSelector(selectInterconnects);

  const interconnect = interconnects[interconnectId];

  const isMapBuilderPage = useMemo(
    () => location.pathname.includes('/map-builder'),
    [location.pathname],
  );

  if (!interconnect) return null;

  const startPoint = points[interconnect.start_point];
  const endPoint = points[interconnect.end_point];

  const constraintsAtStartPoint = constraints[startPoint.id] ?? [];
  const constraintsAtEndPoint = constraints[endPoint.id] ?? [];

  const icConstraints = constraintsAtStartPoint.concat(constraintsAtEndPoint);

  const deleteInterconnect = async () => {
    if (
      confirm(
        `Are you sure you want to delete interconnect ${interconnect.id}?`,
      )
    ) {
      try {
        await deleteInterconnectTrigger(interconnect.id).unwrap();
        closePopup?.();
        toast.success('Interconnect deleted successfully');
      } catch (error) {
        console.error('Failed to delete interconnect:', error);
        toast.error(error?.data?.message ?? 'Failed to delete interconnect');
      }
    }
  };

  const toggleStatus = async (enabled) => {
    if (enabled) {
      await enableInterconnectTrigger(interconnect.id).unwrap();
    } else {
      await disableInterconnectMutation(interconnect.id).unwrap();
    }
  };

  return (
    <>
      <h4 className="text-center bg-light pb-2">
        Interconnect #{interconnect.id}{' '}
        {isMapBuilderPage && isMapLoading === false && (
          <TrashIcon
            className="float-end"
            role="button"
            onClick={deleteInterconnect}
          />
        )}
      </h4>
      <p className="form-check form-switch">
        Status:{' '}
        <span
          style={{
            color: interconnect.enabled ? 'limegreen' : 'red',
            fontWeight: 'bold',
          }}
        >
          {interconnect.enabled ? 'Enabled' : 'Disabled'}{' '}
        </span>
        <input
          role="button"
          type="checkbox"
          className="form-check-input"
          disabled={
            isMapLoading ||
            enableInterconnectRequestInfo.isLoading ||
            disableInterconnectRequestInfo.isLoading
          }
          checked={interconnect.enabled}
          onChange={(evt) => toggleStatus(evt.target.checked)}
        />
      </p>
      <table className="table table-bordered">
        <thead>
          <tr>
            <th></th>
            <th>From</th>
            <th>To</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">TSP</th>
            <td>{startPoint?.tsp_name}</td>
            <td>{endPoint?.tsp_name}</td>
          </tr>
          <tr>
            <th scope="row">Loc Name</th>
            <td>{`#${startPoint.id} ${startPoint.name}`}</td>
            <td>{`#${endPoint.id} ${endPoint.name}`}</td>
          </tr>
          <tr>
            <th scope="row">Loc ID</th>
            <td>{startPoint.loc_id}</td>
            <td>{endPoint.loc_id}</td>
          </tr>
        </tbody>
      </table>

      {icConstraints.length > 0 && (
        <table className="table table-dark">
          <thead>
            <tr>
              <th className="text-center" colSpan="5">
                Constraints
              </th>
            </tr>
            <tr>
              <th>Description</th>
              <th>Priority</th>
              <th>Direction</th>
              <th>Source</th>
              <th>OAC</th>
            </tr>
          </thead>
          <tbody>
            {icConstraints.map((constraint) => {
              const point = points[constraint.point];
              return (
                <tr key={constraint.id}>
                  <td>{constraint.short_description}</td>
                  <td>{constraint.cuts_at_priority}</td>
                  <td>
                    {point.tsp_name}: {titleize(constraint.flow_direction)}
                  </td>
                  <td>
                    {titleize(
                      constraint.source_type?.split('_').join(' ') ?? '',
                    )}
                  </td>
                  <td>{numberFormatter(constraint.max_volume)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
};
